import { useChannelTalk } from '@repo/channel-talk';
import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Checkbox, Divider, Fieldset, List, StickyBottomWrapper, Svg, TextField, useDialog } from '@repo/ui';
import { Title } from '@zenterprise-inc/ui';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import { useMutation } from 'react-relay';

import { TermDrawer } from '../common/drawers/TermDrawer';

import { createPaymentCardMutation } from '@/__generated__/createPaymentCardMutation.graphql';
import { updatePaymentCardMutation } from '@/__generated__/updatePaymentCardMutation.graphql';
import { CARD_PAYMENT_ERROR } from '@/components/tax-refund/common/error/error';
import { createPaymentCard } from '@/graphql/mutation/paymentCard/createPaymentCard';
import { updatePaymentCard } from '@/graphql/mutation/paymentCard/updatePaymentCard';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { HECTO_TERMS } from '@/lib/constants/terms';
import { usePaymentCard } from '@/lib/hooks/use-payment-card';

type TermsKey = (typeof HECTO_TERMS)[number]['key'];
const ALL_KEYS = HECTO_TERMS.map((t) => t.key) as TermsKey[];

export const PaymentCardInputContent = () => {
  const router = useRouter();
  const { sendViewEvent, sendClickEvent } = useUserEventLogger('card-input');
  const { showDialog } = useDialog();
  const { openChannelTalk } = useChannelTalk();

  const { refreshCardData, hasRegisteredCard } = usePaymentCard();
  const [commitCreate, isInFlightCreate] = useMutation<createPaymentCardMutation>(createPaymentCard);
  const [commitUpdate, isInFlightUpdate] = useMutation<updatePaymentCardMutation>(updatePaymentCard);

  const isInFlight = isInFlightCreate || isInFlightUpdate;
  const [termsStatus, setTermsStatus] = useState(Object.fromEntries(ALL_KEYS.map((k) => [k, false])));
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [isCardNumberValid, setIsCardNumberValid] = useState<boolean | undefined>(undefined);
  const [cardNumberError, setCardNumberError] = useState('');
  const [isExpiryDateValid, setIsExpiryDateValid] = useState<boolean | undefined>(undefined);
  const [expiryDateError, setExpiryDateError] = useState('');
  const [cardPassword, setCardPassword] = useState('');
  const [isCardPasswordValid, setIsCardPasswordValid] = useState<boolean | undefined>(undefined);
  const [cardPasswordError, setCardPasswordError] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [isBirthDateValid, setIsBirthDateValid] = useState<boolean | undefined>(undefined);
  const [birthDateError, setBirthDateError] = useState('');
  const [isTermDrawerOpen, setIsTermDrawerOpen] = useState(false);
  const [selectedTermUrl, setSelectedTermUrl] = useState('');

  sendViewEvent('card-input');

  const validateCardNumber = (value: string) => {
    if (value.length === 0) {
      setCardNumberError('카드번호를 입력해주세요.');
      setIsCardNumberValid(false);
      return false;
    }

    if (value.length < 13) {
      setCardNumberError('올바른 카드번호를 입력해주세요.');
      setIsCardNumberValid(false);
      return false;
    }

    setCardNumberError('');
    setIsCardNumberValid(true);
    return true;
  };

  const validateExpiryDate = (value: string) => {
    if (value.length === 0) {
      setExpiryDateError('유효기간을 입력해주세요.');
      setIsExpiryDateValid(false);
      return false;
    }

    const month = parseInt(value.slice(0, 2));
    const year = parseInt(value.slice(2, 4));

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear() % 100;
    const currentMonth = currentDate.getMonth() + 1;

    if (month < 1 || month > 12 || year > currentYear + 10 || value.length < 4) {
      setExpiryDateError('올바른 유효기간을 입력해주세요.');
      setIsExpiryDateValid(false);
      return false;
    }

    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      setExpiryDateError('유효기간이 지났어요.');
      setIsExpiryDateValid(false);
      return false;
    }

    const expiryDate = new Date(2000 + year, month, 0);
    const threeMonthsLater = new Date(currentDate);
    threeMonthsLater.setMonth(currentDate.getMonth() + 3);

    if (expiryDate < threeMonthsLater) {
      setExpiryDateError('90일 이상 남은 카드만 가능해요.');
      setIsExpiryDateValid(false);
      return false;
    }

    setExpiryDateError('');
    setIsExpiryDateValid(true);
    return true;
  };

  const validateCardPassword = (value: string) => {
    if (value.length === 0) {
      setCardPasswordError('비밀번호 앞 2자리를 입력해주세요.');
      setIsCardPasswordValid(false);
      return false;
    }

    if (value.length < 2) {
      setCardPasswordError('올바른 비밀번호를 입력해주세요.');
      setIsCardPasswordValid(false);
      return false;
    }

    setCardPasswordError('');
    setIsCardPasswordValid(true);
    return true;
  };

  const validateBirthDate = (value: string) => {
    if (value.length === 0) {
      setBirthDateError('생년월일을 입력해주세요.');
      setIsBirthDateValid(false);
      return false;
    }

    if (value.length < 6) {
      setBirthDateError('6자리 숫자로 입력해주세요.');
      setIsBirthDateValid(false);
      return false;
    }

    const month = parseInt(value.slice(2, 4));
    const day = parseInt(value.slice(4, 6));

    if (month < 1 || month > 12) {
      setBirthDateError('올바른 생년월일을 입력해주세요.');
      setIsBirthDateValid(false);
      return false;
    }

    if (day < 1 || day > 31) {
      setBirthDateError('올바른 생년월일을 입력해주세요.');
      setIsBirthDateValid(false);
      return false;
    }

    setBirthDateError('');
    setIsBirthDateValid(true);
    return true;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numbers = value.replace(/\D/g, '');

    if (numbers.length <= 16) {
      setCardNumber(numbers);
    }
  };

  const handleCardNumberBlur = () => {
    validateCardNumber(cardNumber);
  };

  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numbers = value.replace(/[^\d]/g, '');

    if (numbers.length <= 4) {
      setExpiryDate(numbers);
    }
  };

  const handleCardPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setCardPassword(value);
  };

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numbers = value.replace(/[^\d]/g, '');

    if (numbers.length <= 6) {
      setBirthDate(numbers);
    }
  };

  const handleExpiryDateBlur = () => {
    validateExpiryDate(expiryDate);
  };

  const handleOpenTerm = (termKey: string) => {
    const termsUrl = `https://bznav.com/popup/terms/${termKey}/`;
    setSelectedTermUrl(termsUrl);
    setIsTermDrawerOpen(true);
  };

  const handleCloseTerm = () => {
    setSelectedTermUrl('');
    setIsTermDrawerOpen(false);
  };

  const handleToggleTerm = (key: TermsKey) => {
    const isChecked = !termsStatus[key];

    sendClickEvent(`card-input_terms_${key}`, { checked: isChecked });

    setTermsStatus((prev) => ({
      ...prev,
      [key]: isChecked
    }));
  };

  const handleClickAllAgreement = () => {
    const allChecked = ALL_KEYS.every((k) => termsStatus[k]);
    const next = !allChecked;

    sendClickEvent('card-input_terms_all', { checked: next });

    setTermsStatus(Object.fromEntries(ALL_KEYS.map((k) => [k, next])) as Record<TermsKey, boolean>);
  };

  const isAllChecked = ALL_KEYS.every((k) => termsStatus[k]);

  const isAllInputsComplete = useMemo(() => {
    return isCardNumberValid && isExpiryDateValid && isCardPasswordValid && isBirthDateValid && isAllChecked;
  }, [isCardNumberValid, isExpiryDateValid, isCardPasswordValid, isBirthDateValid, isAllChecked]);

  const handleSubmit = () => {
    if (!isAllInputsComplete) {
      return;
    }

    sendClickEvent('card-input_confirm');

    if (hasRegisteredCard) {
      commitUpdate({
        variables: {
          updatePaymentCardInput: {
            cardNumber: cardNumber.replace(/\D/g, ''),
            password: cardPassword,
            birth: birthDate,
            expireYear: expiryDate.slice(2, 4),
            expireMonth: expiryDate.slice(0, 2)
          }
        },
        onCompleted: (response) => {
          handleQueryCompleted(response.updatePaymentCard);
        },
        onError: (error) => {
          console.error(error);
        }
      });
    } else {
      commitCreate({
        variables: {
          paymentCardInfoInput: {
            cardNumber: cardNumber.replace(/\D/g, ''),
            password: cardPassword,
            birth: birthDate,
            expireYear: expiryDate.slice(2, 4),
            expireMonth: expiryDate.slice(0, 2)
          }
        },
        onCompleted: (response) => {
          handleQueryCompleted(response.createPaymentCard);
        },
        onError: (error) => {
          console.error(error);
        }
      });
    }
  };

  const handleQueryCompleted = async (data: any) => {
    if (data.result) {
      await refreshCardData();
      router.back();
    } else if (data.errors && data.errors.code) {
      handleQueryError(data.errors);
    }
  };

  const handleQueryError = (error: any) => {
    const errorInfo = CARD_PAYMENT_ERROR[error?.code] || CARD_PAYMENT_ERROR['ERR_HECTO_ETC'];
    const shouldShowInquiry = (error.code === 'ERR_ETC' && !error.message) || error.code === 'ERR_HECTO_ETC';

    let eventAttrValue = '';
    switch (error.code) {
      case 'ERR_CARD_NUMBER':
        eventAttrValue = 'card-number';
        break;
      case 'ERR_CARD_EXPIRED':
        eventAttrValue = 'expiration';
        break;
      case 'ERR_CARD_PASSWORD':
        eventAttrValue = 'password';
        break;
      case 'ERR_CARD_PASSWORD_FIVE_TIMES':
        eventAttrValue = 'error-count';
        break;
      case 'ERR_CARD_BIRTH':
        eventAttrValue = 'birthday';
        break;
      case 'ERR_HECTO_ETC':
        eventAttrValue = 'etc';
        break;
      case 'ERR_ETC':
        eventAttrValue = 'other';
        break;
      default:
        break;
    }

    sendViewEvent('card-error_dialog', {
      error: eventAttrValue,
      ...(error.code === 'ERR_ETC' && error.message && { message: error.message })
    });

    showDialog({
      title: (error.code === 'ERR_ETC' && error.message) || errorInfo?.title,
      description: error.code === 'ERR_ETC' && error.message ? '' : errorInfo?.description,
      buttons: {
        primary: {
          text: '확인'
        },
        ...(shouldShowInquiry && {
          secondary: {
            text: '문의하기',
            onClick: () => {
              openChannelTalk(SUPPORT_BOT_LINK.home);
            }
          }
        })
      }
    });
  };

  return (
    <div className="page-padding flex w-full flex-col pb-24">
      <Title.Headline className="py-[24px]" size="large" title="카드 정보를 입력해주세요" />
      <div className={'pt-2 pb-normal-large'}>
        <Fieldset label="카드번호" isInvalid={isCardNumberValid === false} errorMessage={cardNumberError}>
          <TextField
            placeholder="0000-0000-0000-0000"
            inputMode={'numeric'}
            maxLength={19}
            value={
              cardNumber
                .replace(/\D/g, '')
                .match(/.{1,4}/g)
                ?.join('-') || ''
            }
            onChange={handleCardNumberChange}
            onBlur={handleCardNumberBlur}
            error={isCardNumberValid === false}
          />
        </Fieldset>
        <div className="flex gap-4">
          <Fieldset label="유효기간" className="flex-1" isInvalid={isExpiryDateValid === false} errorMessage={expiryDateError}>
            <TextField
              placeholder="MM/YY"
              inputMode={'numeric'}
              maxLength={5}
              value={
                expiryDate
                  .replace(/\D/g, '')
                  .match(/.{1,2}/g)
                  ?.join('/') || ''
              }
              onChange={handleExpiryDateChange}
              onBlur={handleExpiryDateBlur}
              error={isExpiryDateValid === false}
            />
          </Fieldset>
          <Fieldset label="비밀번호 앞 2자리" className="flex-1" isInvalid={isCardPasswordValid === false} errorMessage={cardPasswordError}>
            <TextField
              placeholder="••"
              inputMode="numeric"
              type="password"
              maxLength={2}
              value={cardPassword}
              onChange={handleCardPasswordChange}
              onBlur={() => validateCardPassword(cardPassword)}
              error={isCardPasswordValid === false}
            />
          </Fieldset>
        </div>
        <Fieldset label="생년월일" isInvalid={isBirthDateValid === false} errorMessage={birthDateError}>
          <TextField
            placeholder="6자리(YYMMDD)"
            inputMode={'numeric'}
            maxLength={6}
            value={birthDate}
            onChange={handleBirthDateChange}
            onBlur={() => validateBirthDate(birthDate)}
            error={isBirthDateValid === false}
          />
        </Fieldset>
      </div>
      <List
        className={'p-normal-medium'}
        size="medium"
        title="필수 약관 동의"
        primaryAction
        onClick={handleClickAllAgreement}
        leadingElement={
          <Checkbox
            isBox
            checked={isAllChecked}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleClickAllAgreement();
            }}
          />
        }
      />
      <Divider />
      <div className={'py-tiny-large'}>
        {HECTO_TERMS.map((terms) => {
          const checked = termsStatus[terms.key as TermsKey] || false;
          return (
            <List
              key={terms.key}
              size="xsmall"
              title={terms.title}
              leadingElement={
                <Checkbox
                  checked={checked}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleToggleTerm(terms.key);
                  }}
                />
              }
              secondaryAction={
                <Svg
                  src="ic_chevron-right.svg"
                  width={16}
                  height={16}
                  color="#8A96A3"
                  onClick={() => {
                    handleOpenTerm(terms.key);
                  }}
                />
              }
              primaryAction
              onClick={() => handleToggleTerm(terms.key)}
            />
          );
        })}
      </div>
      <StickyBottomWrapper>
        <BoxButton className={'w-full'} size={'large'} disabled={!isAllInputsComplete || isInFlight} onClick={handleSubmit}>
          {isAllInputsComplete ? '입력 완료' : '정보를 모두 입력해주세요'}
        </BoxButton>
      </StickyBottomWrapper>

      <TermDrawer isOpen={isTermDrawerOpen} handleClose={handleCloseTerm} url={selectedTermUrl} />
    </div>
  );
};
